import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Snackbar,
  Box,
} from '@material-ui/core';
import * as React from 'react';
import { DelayedLinearProgress } from 'components';
import { ShopService } from 'services';
import { OpeningHour, Shop } from 'interfaces';

/**
 * Opening hours editor scoped to the reservation system. Edits the
 * To Go shop's opening_hours rows (same table the website-facing
 * availability endpoint reads from). Lives inside Reservations
 * Settings per user 2026-05-28 ask.
 *
 * Day-of-week convention is ISO: 1 = Monday, 7 = Sunday.
 */

const TO_GO_SHOP_ID = 2;

interface DayRow {
  dayOfWeek: number;
  enabled: boolean;
  fromTime: string;
  toTime: string;
}

const DAY_LABELS: { dow: number; label: string }[] = [
  { dow: 1, label: 'Monday' },
  { dow: 2, label: 'Tuesday' },
  { dow: 3, label: 'Wednesday' },
  { dow: 4, label: 'Thursday' },
  { dow: 5, label: 'Friday' },
  { dow: 6, label: 'Saturday' },
  { dow: 7, label: 'Sunday' },
];

const trimTime = (t?: string | null) => (t ? t.slice(0, 5) : '');

export const OpeningHoursTab: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<DayRow[]>(
    DAY_LABELS.map((d) => ({
      dayOfWeek: d.dow,
      enabled: false,
      fromTime: '',
      toTime: '',
    })),
  );

  const load = React.useCallback(() => {
    setLoading(true);
    ShopService.getShops()
      .then((shops) => {
        const list = (shops as Shop[]) || [];
        const toGo = list.find((s) => s.id === TO_GO_SHOP_ID);
        const hours: OpeningHour[] = toGo?.openingHours || [];
        setRows(
          DAY_LABELS.map((d) => {
            const match = hours.find(
              (h) => (h as any).dayOfWeek === d.dow,
            );
            if (match && match.fromTime && match.toTime) {
              return {
                dayOfWeek: d.dow,
                enabled: true,
                fromTime: trimTime(match.fromTime),
                toTime: trimTime(match.toTime),
              };
            }
            return {
              dayOfWeek: d.dow,
              enabled: false,
              fromTime: '',
              toTime: '',
            };
          }),
        );
      })
      .catch(() => {
        // leave rows in default empty state
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const setRow = (dow: number, patch: Partial<DayRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.dayOfWeek === dow ? { ...r, ...patch } : r)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // IMPORTANT: Cake's time marshaller silently drops HH:MM input and
      // only accepts HH:MM:SS. Found by testing patchEntity against the
      // shop entity directly: '09:00' yields entity.from_time = null with
      // no validation error raised. Appending ':00' here makes the save
      // actually persist the times.
      const openingHours = rows
        .filter((r) => r.enabled && r.fromTime && r.toTime)
        .map((r) => ({
          day_of_week: r.dayOfWeek,
          from_time: r.fromTime + ':00',
          to_time: r.toTime + ':00',
        }));
      const result = await ShopService.save({
        id: TO_GO_SHOP_ID,
        data: { opening_hours: openingHours },
      });
      if (result) {
        setSnackbar('Opening hours saved');
        load();
      } else {
        setSnackbar('Save returned no result');
      }
    } catch (e) {
      setSnackbar((e as Error)?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Paper style={{ padding: 16 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Opening hours used by the reservation system. Disable a day or
          leave its times empty and that weekday will be closed for
          bookings. Per-date overrides live in <strong>Special Days</strong>.
        </Typography>
        {loading && <DelayedLinearProgress />}
        <Grid container spacing={1} style={{ marginTop: 8 }}>
          {rows.map((r) => {
            const label = DAY_LABELS.find((d) => d.dow === r.dayOfWeek)?.label;
            return (
              <Grid item xs={12} key={r.dayOfWeek}>
                <Paper
                  variant="outlined"
                  style={{
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography style={{ width: 100, fontWeight: 600 }}>
                    {label}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={r.enabled}
                        onChange={(_, v) => setRow(r.dayOfWeek, { enabled: v })}
                      />
                    }
                    label={r.enabled ? 'Open' : 'Closed'}
                  />
                  <TextField
                    label="From"
                    type="time"
                    value={r.fromTime}
                    onChange={(e) =>
                      setRow(r.dayOfWeek, { fromTime: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    disabled={!r.enabled}
                    size="small"
                    style={{ width: 140 }}
                  />
                  <TextField
                    label="To"
                    type="time"
                    value={r.toTime}
                    onChange={(e) =>
                      setRow(r.dayOfWeek, { toTime: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    disabled={!r.enabled}
                    size="small"
                    style={{ width: 140 }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            Save
          </Button>
        </Box>
      </Paper>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </React.Fragment>
  );
};
